/**
 * Keyboard reading shared by every view that walks a survivor around
 * (settlement, arena, raid locations, tutorial).
 *
 * ─── Physical keys, not characters ──────────────────────────────────────────
 * `e.key` is the CHARACTER the layout types. On a Russian layout the W key
 * types "ц", so a set keyed on `e.key` never held "w" and the survivor could
 * not walk (Janeks, 2026-10-20, mid-duel). Worse, a key pressed before a
 * layout switch and released after it reports two different characters, so
 * the keyup deleted "ы" while "s" stayed in the set and the survivor walked
 * down forever. `e.code` names the key's POSITION, so WASD, E, I, C and the
 * window shortcuts stay where they are on AZERTY, Russian or anything else.
 *
 * ─── Held keys that can no longer be trusted ────────────────────────────────
 * A held key loses its keyup when focus leaves the page (Cmd+Tab, Cmd+Space
 * for the layout switch or Spotlight, a system dialog). macOS also fires no
 * keyup for letters released while Cmd is down. Each of those leaves a key
 * "held" with no way to let go, so `trackHeldKeys` empties the set then.
 */

/**
 * The layout-independent name of a key, lowercase: "w", "e", "1",
 * "arrowup", "escape". Letters and digits come from `e.code`; everything
 * else (arrows, Escape, Enter, F-keys) is the same in every layout, so
 * `e.key` is kept for those.
 */
export function keyName(e: Pick<KeyboardEvent, 'key' | 'code'>): string {
  const code = e.code ?? '';
  const letter = /^Key([A-Z])$/.exec(code);
  if (letter) return letter[1].toLowerCase();
  const digit = /^(?:Digit|Numpad)([0-9])$/.exec(code);
  if (digit) return digit[1];
  return (e.key ?? '').toLowerCase();
}

/**
 * Wires `keys` to be emptied whenever held keys stop being trustworthy (see
 * the header). Returns the cleanup that removes the listeners.
 */
export function trackHeldKeys(keys: Set<string>): () => void {
  const clear = (): void => keys.clear();
  const onVisibility = (): void => { if (document.hidden) clear(); };
  const onKeyUp = (e: KeyboardEvent): void => { if (e.key === 'Meta') clear(); };
  window.addEventListener('blur', clear);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('keyup', onKeyUp);
  return () => {
    window.removeEventListener('blur', clear);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('keyup', onKeyUp);
  };
}
